/**
 * Organization Preferences (Practice Settings)
 *
 * Consolidated, table-styled list of organization-wide defaults — mirrors the
 * PermissionsMatrix card/table treatment so preferences read as one coherent
 * section rather than a series of one-off cards. Add future org-level
 * preferences as additional rows here.
 */
import { type ReactNode, useCallback } from 'react';
import {
  useBlockSigningUntilPlaceholdersFilled,
  useNoteSigningEnabled,
  useRequireScheduledAppointmentForNoteCreation,
} from '@features/user';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  useGetPatientVisibilityQuery,
  useUpdateOrganizationPreferencesMutation,
  useUpdatePatientVisibilityMutation,
} from '../store/api';

import { SectionCardTitle } from './SectionCardTitle';

interface OrganizationPreferencesTableProps {
  /** Guide control, inline after the card title (GDN-3703). */
  titleControl?: ReactNode;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = (error as { data?: { message?: unknown } }).data;
    if (typeof data?.message === 'string') return data.message;
  }
  return fallback;
}

export function OrganizationPreferencesTable({
  titleControl,
}: OrganizationPreferencesTableProps = {}) {
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: visibilityData,
    isLoading: isVisibilityLoading,
    refetch: refetchVisibility,
  } = useGetPatientVisibilityQuery();
  const [updateVisibility, { isLoading: isUpdatingVisibility }] =
    useUpdatePatientVisibilityMutation();

  const blockSigning = useBlockSigningUntilPlaceholdersFilled();
  const noteSigningEnabled = useNoteSigningEnabled();
  const requireScheduledAppointmentForNoteCreation =
    useRequireScheduledAppointmentForNoteCreation();
  const [updatePreferences, { isLoading: isUpdatingPreferences }] =
    useUpdateOrganizationPreferencesMutation();

  const currentMode = visibilityData?.mode || 'provider_only';

  const handleVisibilityChange = useCallback(
    async (event: SelectChangeEvent) => {
      try {
        const result = await updateVisibility({
          providerUpdatePatientVisibilityRequest: { mode: event.target.value },
        }).unwrap();
        if (result.success) {
          enqueueSnackbar(result.message || 'Patient visibility updated', {
            variant: 'success',
          });
          refetchVisibility();
        }
      } catch (error: unknown) {
        enqueueSnackbar(getErrorMessage(error, 'Failed to update patient visibility'), {
          variant: 'error',
        });
      }
    },
    [updateVisibility, enqueueSnackbar, refetchVisibility],
  );

  const handleNoteSigningChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.checked;
      try {
        await updatePreferences({
          updateOrganizationPreferencesRequest: {
            block_signing_until_placeholders_filled: nextValue,
          },
        }).unwrap();
        enqueueSnackbar(
          nextValue
            ? 'Providers will now be reminded to fill placeholders before signing.'
            : 'Placeholder reminder before signing turned off.',
          { variant: 'success' },
        );
      } catch {
        enqueueSnackbar('Failed to update setting. Please try again.', {
          variant: 'error',
        });
      }
    },
    [updatePreferences, enqueueSnackbar],
  );

  const handleNoteSigningEnabledChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.checked;
      try {
        await updatePreferences({
          updateOrganizationPreferencesRequest: {
            // Field ships with the same release as the backend; regenerate the
            // API slice (pnpm codegen) after deploy to drop this cast.
            note_signing_enabled: nextValue,
          },
        }).unwrap();
        enqueueSnackbar(
          nextValue
            ? 'Providers will now sign notes to finalize them.'
            : 'Providers will now complete notes without a signature.',
          { variant: 'success' },
        );
      } catch {
        enqueueSnackbar('Failed to update setting. Please try again.', {
          variant: 'error',
        });
      }
    },
    [updatePreferences, enqueueSnackbar],
  );

  const handleRequireScheduledAppointmentChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.checked;
      try {
        await updatePreferences({
          updateOrganizationPreferencesRequest: {
            require_scheduled_appointment_for_note_creation: nextValue,
          },
        }).unwrap();
        enqueueSnackbar(
          nextValue
            ? 'Recording and note generation now require a scheduled appointment.'
            : 'Scheduled appointment requirement turned off.',
          { variant: 'success' },
        );
      } catch {
        enqueueSnackbar('Failed to update setting. Please try again.', {
          variant: 'error',
        });
      }
    },
    [updatePreferences, enqueueSnackbar],
  );

  if (isVisibilityLoading) {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Loading preferences...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
      <CardHeader
        avatar={<TuneIcon color="primary" />}
        title={<SectionCardTitle title="Preferences" control={titleControl} />}
        slotProps={{ title: { variant: 'h6', fontWeight: 600 } }}
        subheader="Organization-wide defaults applied to all providers in your practice."
      />

      <CardContent sx={{ pt: 0 }}>
        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '64%' }} />
              <col style={{ width: '36%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Preference</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Setting
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Patient visibility
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    How patient information is shared across your organization. Applies to all
                    providers immediately.
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Select
                    size="small"
                    value={currentMode}
                    disabled={isUpdatingVisibility}
                    onChange={handleVisibilityChange}
                    sx={{ minWidth: 180 }}
                    aria-label="Patient visibility mode"
                  >
                    <MenuItem value="provider_only">Provider only</MenuItem>
                    <MenuItem value="organization_wide">Organization wide</MenuItem>
                  </Select>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Note signing
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Providers electronically sign notes to finalize them (&ldquo;Sign Note&rdquo;).
                    When off, notes are simply marked complete without a signature.
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Switch
                    checked={noteSigningEnabled}
                    disabled={isUpdatingPreferences}
                    onChange={handleNoteSigningEnabledChange}
                    slotProps={{
                      input: {
                        'aria-label': 'Providers sign notes to finalize them',
                      },
                    }}
                  />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Fill placeholders before signing
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Remind providers to fill in any bracketed placeholders (text in [brackets])
                    before signing a note. They can still sign if needed.
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Switch
                    checked={blockSigning}
                    disabled={isUpdatingPreferences}
                    onChange={handleNoteSigningChange}
                    slotProps={{
                      input: {
                        'aria-label': 'Remind providers to fill placeholders before signing',
                      },
                    }}
                  />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Require scheduled appointment for recording and notes
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Only allow recording and note generation for encounters tied to an existing
                    scheduled appointment.
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Switch
                    checked={requireScheduledAppointmentForNoteCreation}
                    disabled={isUpdatingPreferences}
                    onChange={handleRequireScheduledAppointmentChange}
                    slotProps={{
                      input: {
                        'aria-label':
                          'Require scheduled appointment for recording and note generation',
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}
